import React, { useState } from "react";
import OrderInformation from "./OrderInformation";
import ShippingInformation from "./ShippingInformation";
import ProductInformation from "./ProductInformation";
import { Check } from "lucide-react";
import { useNavigate } from "react-router";
import { useCreateOrder } from "../../../../api/orders/orders";

const AddNewOrder = () => {
  const [step, setStep] = useState(1);
  const { mutate: createOrder, isLoading } = useCreateOrder();
  const [formData, setFormData] = useState({
    orderNumber: "",
    orderType: "",
    paymentType: "",
    shipmentType: "",
    shippingInfo: {
      CustomerName: "",
      mobile: "",
      email: "",
      address: "",
      address2: "",
      state: "",
      city: "",
      Pincode: "",
    },
    productInfo: [
      {
        productName: "",
        quantity: "",
        price: "",
        discount: "",
        sku: "",
      },
    ],
    dimensions: {
      weight: "",
      length: "",
      breadth: "",
      height: "",
    },
    paymentData: {
      paymentMethods: [], // ✅ Changed to array
      totalAmount: 0,
    },
  });

  const navigate = useNavigate();

  const steps = [
    { title: "Step 1", subtitle: "Order Details" },
    { title: "Step 2", subtitle: "Shipping Details" },
    { title: "Step 3", subtitle: "Product Details" },
  ];

  const goNext = (dataFromStep) => {
    if (step === 3) {
      const updatedData = {
        ...formData,
        productInfo: dataFromStep.productInfo,
        dimensions: dataFromStep.dimensions,
        totalAmount: dataFromStep.totalAmount,
        paymentData: dataFromStep.paymentData,
      };

      setFormData(updatedData);
      handleFinalSubmit(updatedData);
    } else {
      setFormData((prev) => {
        let updatedData = { ...prev };

        if (step === 1) {
          updatedData = { ...updatedData, ...dataFromStep };
        } else if (step === 2) {
          updatedData.shippingInfo = dataFromStep.shippingInfo;
        }

        return updatedData;
      });

      setStep((prev) => prev + 1);
    }
  };

  const goBack = () => setStep((prev) => Math.max(prev - 1, 1));
  const handleGoBack = () => navigate("/orders");

  const handleFinalSubmit = (formData) => {
    if (!formData) return console.error("formData is undefined!");

    // Default payment methods (always included)
    const defaultPaymentMethods = [
      {
        paymentOption: "Clicktocart",
        paymentAmount: 0,
        paymentProofImages: [],
      },
      {
        paymentOption: "Swapnil Gautam",
        paymentAmount: 0,
        paymentProofImages: [],
      },
    ];

    // Merge user-selected methods with defaults
    const mergedPaymentMethods = defaultPaymentMethods.map((defaultMethod) => {
      const existing = formData.paymentData.paymentMethods?.find(
        (m) => m.paymentOption === defaultMethod.paymentOption
      );
      return existing
        ? {
            ...defaultMethod,
            paymentAmount: existing.paymentAmount ,
            paymentProofImages: existing.paymentProofImages ,
          }
        : defaultMethod;
    });

    // Build the orderData object
    const orderData = {
      PaymentType: formData.paymentType?.toLowerCase() ,
      OrderType: formData.orderType?.toLowerCase() ,
      CustomerName: formData.shippingInfo.CustomerName,
      OrderNumber: formData.orderNumber,
      Weight: Number(formData.dimensions.weight),
      Length: Number(formData.dimensions.length),
      Breadth: Number(formData.dimensions.breadth),
      Height: Number(formData.dimensions.height),
      ProductDetails: formData.productInfo.map((p) => ({
        Name: p.productName,
        SKU: p.sku,
        QTY: Number(p.quantity),
        GST: 0,
        Price: Number(p.price),
      })),
      InvoiceAmount: Number(formData.totalAmount),
      EwayBill: null,
      ShippingCharge: 0,
      CodCharge: null,
      Discount: 0,
      Addresses: {
        BilingAddress: {
          AddressLine1: formData.shippingInfo.address,
          AddressLine2: formData.shippingInfo.address2 ,
          Email: formData.shippingInfo.email,
          City: formData.shippingInfo.city,
          State: formData.shippingInfo.state,
          Country: "India",
          Pincode: formData.shippingInfo.Pincode,
          ContactCode: "91",
          Contact: formData.shippingInfo.mobile,
        },
        ShippingAddress: {
          AddressLine1: formData.shippingInfo.address,
          AddressLine2: formData.shippingInfo.address2 ,
          Email: formData.shippingInfo.email,
          City: formData.shippingInfo.city,
          State: formData.shippingInfo.state,
          Country: "India",
          Pincode: formData.shippingInfo.Pincode,
          ContactCode: "91",
          Contact: formData.shippingInfo.mobile,
        },
        PickupAddress: {
          warehouseCode: "TRL Warehouse_Kathwada_SC09204",
          WarehouseName: "TRL Warehouse_Kathwada",
          ContactName: "Monik Radadiya",
          AddressLine1:
            "21, Victoria Industrial Park, Road No 5, Near Kotak Mahindra Bank, Kathwada GIDC",
          AddressLine2: "",
          City: "AHMEDABAD",
          State: "GUJARAT",
          Country: "India",
          Pincode: "382430",
          ContactCode: "91",
          Contact: "9023734056",
        },
      },
      paymentMethods: mergedPaymentMethods, // ✅ Always include both
      totalAmount:
        formData.paymentData?.totalAmount ||
        mergedPaymentMethods.reduce((sum, m) => sum + m.paymentAmount, 0),
    };

    // ✅ Build FormData
    const submitData = new FormData();
    submitData.append("orderData", JSON.stringify(orderData));

    // ✅ Attach images (only if any exist)
    mergedPaymentMethods.forEach((method) => {
      if (method.paymentProofImages?.length > 0) {
        method.paymentProofImages.forEach((file, i) => {
          const sanitized = method.paymentOption.replace(/\s+/g, "_");
          const fieldName = `paymentProofImages_${sanitized}_${i}`;
          submitData.append(fieldName, file);
          console.log(`📸 Added file: ${fieldName}`);
        });
      }
    });

    console.log("📦 Submitting Order:", orderData);
    createOrder(submitData);
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-auto mx-auto bg-white rounded-lg shadow-md flex h-[100vh] overflow-hidden">
        {/* Sidebar Stepper */}
        <div className="hidden md:block md:w-1/5 p-6 border-r overflow-y-auto">
          <div className="flex flex-col">
            {steps.map((s, index) => (
              <div key={index} className="flex items-start space-x-3 relative">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-7 h-7 text-sm rounded-full flex items-center justify-center text-white font-medium ${
                      index + 1 === step
                        ? "bg-primary"
                        : index + 1 < step
                        ? "bg-primary"
                        : "bg-gray-300"
                    }`}
                  >
                    {index + 1 < step ? <Check size={14} /> : index + 1}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`w-px h-16 ${
                        index + 1 < step ? "bg-primary" : "bg-gray-300"
                      }`}
                    ></div>
                  )}
                </div>
                <div>
                  <h4 className="text-base font-medium">{s.title}</h4>
                  <p className="text-xs text-gray-600">{s.subtitle}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="w-full md:w-4/5 flex flex-col overflow-y-auto">
          {/* Header */}
          <div className="border-b p-4 md:p-6 sticky top-0 bg-white z-10 flex justify-between items-center">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800">
              Add New Order
            </h2>
            <button
              onClick={handleGoBack}
              className="bg-primaryLight hover:bg-primary text-primary border border-primary hover:text-white px-4 py-1.5 rounded text-sm"
            >
              ⤺ GO BACK
            </button>
          </div>

          {/* Step Content */}
          <div className="p-4 md:p-6 flex-grow overflow-y-auto text-sm">
            {step === 1 && (
              <OrderInformation onNext={goNext} defaultData={formData} />
            )}
            {step === 2 && (
              <ShippingInformation
                onPrevious={goBack}
                onNext={goNext}
                defaultData={formData.shippingInfo}
              />
            )}
            {step === 3 && (
              <ProductInformation
                onPrevious={goBack}
                onNext={(data) => goNext(data)}
                defaultData={formData}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewOrder;
